import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Gauge, ShieldCheck, Calendar, Clock, AlertTriangle } from 'lucide-react';
import { PlayerRanking } from '@/types/stats';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { useLanguage } from '@/hooks/use-language';
import { useEvents } from '@/hooks/use-events';
import { usePenalty } from '@/hooks/use-penalty';

export function ProfileStats() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { events } = useEvents();
  const { getMemberPenalty } = usePenalty();

  const [playerRanking, setPlayerRanking] = React.useState<PlayerRanking | null>(null);
  const [loadingRanking, setLoadingRanking] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const loadRanking = async () => {
      if (!user) return;
      setLoadingRanking(true);
      try {
        const { data, error } = await supabase
          .from('player_rankings_v')
          .select(
            'user_id, name, rating, rating_deviation, reliability, level, matches_played, events_played, events_unique, last_event_at, last_rating_delta, phone'
          )
          .eq('user_id', user.id)
          .maybeSingle();

        if (cancelled) return;

        if (error) {
          console.warn('ProfileStats: failed to load ranking', error);
          setPlayerRanking(null);
        } else if (data) {
          setPlayerRanking({
            player_id: data.user_id ?? user.id,
            member_id: user.id,
            name: data.name ?? user.firstName,
            phone: data.phone,
            rating: Number(data.rating ?? 1500),
            rating_deviation: Number(data.rating_deviation ?? 350),
            reliability: Number(data.reliability ?? 0.5),
            level: (data.level as any) ?? 'level_1',
            matches_played: Number(data.matches_played ?? 0),
            tournament_matches: Number(data.events_unique ?? 0),
            last_activity_at: data.last_event_at ?? null,
            last_rating_delta: Number(data.last_rating_delta ?? 0),
          });
        } else {
          setPlayerRanking(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('ProfileStats: error loading ranking', error);
          setPlayerRanking(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingRanking(false);
        }
      }
    };

    void loadRanking();

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user) return null;

  const userPenalty = getMemberPenalty(user.id);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const signedEvents = events.filter((e) => e.participants.includes(user.id) && new Date(e.date) >= today);
  const waitingListEvents = events.filter((e) => (e.waitingList || []).includes(user.id) && new Date(e.date) >= today);

  const levelLabels = language === 'no'
    ? {
        level_1: 'Nivå 1',
        level_2: 'Nivå 2',
        level_3: 'Nivå 3',
        level_4: 'Nivå 4',
      }
    : {
        level_1: 'Level 1',
        level_2: 'Level 2',
        level_3: 'Level 3',
        level_4: 'Level 4',
      };

  const reliabilityPercent = playerRanking
    ? Math.round(Math.max(0, Math.min(0.99, playerRanking.reliability ?? 0)) * 100)
    : null;
  const ratingDelta = playerRanking ? playerRanking.last_rating_delta : 0;

  const stats = [
    {
      key: 'rating',
      title: language === 'no' ? 'Rating' : 'Rating',
      value: playerRanking ? Math.round(playerRanking.rating) : loadingRanking ? '…' : '—',
      description: playerRanking ? levelLabels[playerRanking.level] : language === 'no' ? 'Ingen rating' : 'No rating yet',
      icon: Gauge,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      badge:
        playerRanking && Math.abs(ratingDelta) >= 0.05
          ? `${ratingDelta > 0 ? '+' : ''}${ratingDelta.toFixed(1)}`
          : null,
      badgeClass:
        ratingDelta > 0
          ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30'
          : ratingDelta < 0
          ? 'bg-rose-500/10 text-rose-600 border-rose-500/30'
          : 'text-muted-foreground border-border',
    },
    {
      key: 'confidence',
      title: language === 'no' ? 'Pålitelighet' : 'Confidence',
      value: reliabilityPercent !== null ? `${reliabilityPercent}%` : loadingRanking ? '…' : '—',
      description: language === 'no' ? 'rating-sikkerhet' : 'rating confidence',
      icon: ShieldCheck,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
    {
      key: 'signups',
      title: language === 'no' ? 'Aktive Påmeldinger' : 'Active Signups',
      value: signedEvents.length,
      description: language === 'no' ? 'arrangementer' : 'events',
      icon: Calendar,
      color: 'text-group-advanced',
      bgColor: 'bg-group-advanced/10',
    },
    {
      key: 'waitlist',
      title: language === 'no' ? 'Venteliste' : 'Waiting List',
      value: waitingListEvents.length,
      description: language === 'no' ? 'arrangementer' : 'events',
      icon: Clock,
      color: 'text-group-intermediate',
      bgColor: 'bg-group-intermediate/10',
    },
    {
      key: 'penalty',
      title: language === 'no' ? 'Straff Poeng' : 'Penalty Points',
      value: userPenalty?.totalDots || 0,
      description: language === 'no' ? 'prikker' : 'dots',
      icon: AlertTriangle,
      color: userPenalty?.totalDots ? 'text-destructive' : 'text-success',
      bgColor: userPenalty?.totalDots ? 'bg-destructive/10' : 'bg-success/10',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <Card key={stat.key} className="hover:shadow-md transition-shadow duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <div className={`p-2 rounded-md ${stat.bgColor}`}>
                <Icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline space-x-2">
                <div className="text-2xl font-bold text-foreground">{stat.value}</div>
                <span className="text-sm text-muted-foreground">{stat.description}</span>
              </div>
              {'badge' in stat && stat.badge && (
                <Badge
                  variant="outline"
                  className={`mt-2 text-xs ${'badgeClass' in stat && stat.badgeClass ? stat.badgeClass : ''}`}
                >
                  {stat.badge}
                </Badge>
              )}
              {stat.title.includes('Penalty') && userPenalty?.totalDots === 0 && (
                <Badge variant="outline" className="mt-2 text-xs text-success">
                  {language === 'no' ? 'Ren rulleblad' : 'Clean record'}
                </Badge>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
